<script setup lang="ts">
import { computed } from 'vue'
import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import Checkbox from '@/components/ui/checkbox/Checkbox.vue'
import MercariFilterCommon, {
	type FilterType,
} from './_MercariFilterCommon.vue'

const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	filterContent: {
		type: Object,
		required: true,
	},
	filterInstance: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['update:isOpen'])

const {
	tempFilterState,
	isTempMinPriceValid,
	isTempMaxPriceValid,
	isTempPriceRangeValid,
	hasTempPriceError,
	handleTempPriceChange,
} = props.filterInstance

const {
	handleCheckboxChange,
	handleCategorySelect,
	handleClear,
	handleApply,
	handleOpenChange,
} = props.filterInstance.useFilterPanelLogic()

const selectedCategory = computed(() => {
	return tempFilterState.category || 'all'
})

const scrollBarClasses = [
	'[scrollbar-gutter:stable]',
	'[&::-webkit-scrollbar]:w-[12px] [&::-webkit-scrollbar]:bg-transparent',
	'[&::-webkit-scrollbar-track]:my-[24px]',
	'[&::-webkit-scrollbar-thumb]:bg-n-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-clip-padding',
	'[&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-[4px] [&::-webkit-scrollbar-thumb]:border-transparent',
]

const handleClose = () => {
	emit('update:isOpen', false)
}
</script>

<template>
	<Sheet :open="isOpen" @update:open="value => handleOpenChange(value, emit)">
		<SheetContent side="bottom" class="p-0">
			<div class="flex h-[calc(100dvh-48px)] flex-col rounded-t-lg bg-white">
				<header
					class="flex items-center justify-between rounded-lg border-b border-n-200 px-20px py-16px"
				>
					<SheetClose @click="handleClose" class="invisible">
						<BaseIcon name="close" class="h-24px w-24px" />
					</SheetClose>
					<div class="typography-h5-str text-center">
						{{ filterContent.title }}
					</div>
					<SheetClose @click="handleClose" class="p-0">
						<BaseIcon name="close" class="h-16px w-16px" />
					</SheetClose>
				</header>

				<main
					class="flex flex-1 flex-col overflow-y-auto p-16px pr-12px"
					:class="scrollBarClasses"
				>
					<MercariFilterCommon
						:filter-content="filterContent"
						:filter-instance="props.filterInstance"
						:is-bottom-sheet="true"
						@checkbox-change="handleCheckboxChange"
						@category-select="handleCategorySelect"
					>
						<template #checkbox="{ option, checked, onChange }">
							<Checkbox
								:checked="checked"
								:value="option.id"
								@update:checked="onChange"
							/>
						</template>
						<template #price>
							<div class="border-b border-n-200 py-20px">
								<h3 class="typography-b2-str mb-16px text-n-950">
									{{ filterContent.price.title }}
								</h3>
								<div class="space-y-12px">
									<input
										type="text"
										:value="tempFilterState.price.min"
										@input="(e: Event) => handleTempPriceChange('min', e)"
										:placeholder="filterContent.price.options[0].label"
										inputmode="numeric"
										pattern="[0-9]*"
										class="w-full rounded-lg border border-n-300 px-16px py-12px"
										:class="{
											'border-r-700':
												!isTempMinPriceValid || !isTempPriceRangeValid,
										}"
									/>
									<input
										type="text"
										:value="tempFilterState.price.max"
										@input="(e: Event) => handleTempPriceChange('max', e)"
										:placeholder="filterContent.price.options[1].label"
										inputmode="numeric"
										pattern="[0-9]*"
										class="w-full rounded-lg border border-n-300 px-16px py-12px"
										:class="{
											'border-r-700':
												!isTempMaxPriceValid || !isTempPriceRangeValid,
										}"
									/>
									<p
										class="typography-c1-reg"
										:class="hasTempPriceError ? 'text-r-700' : 'text-n-500'"
									>
										{{ filterContent.price.description }}
									</p>
								</div>
							</div>
						</template>
					</MercariFilterCommon>
				</main>

				<footer class="mt-auto border-t border-n-200 p-16px">
					<div class="grid grid-cols-2 gap-16px">
						<BaseButton
							variant="secondary"
							size="medium"
							@click="() => handleClear(emit)"
						>
							{{ filterContent.buttons.clear }}
						</BaseButton>
						<BaseButton
							variant="primary"
							size="medium"
							@click="() => handleApply(emit)"
						>
							{{ filterContent.buttons.apply }}
						</BaseButton>
					</div>
				</footer>
			</div>
		</SheetContent>
	</Sheet>
</template>
<style scoped></style>
